import { Component, computed, inject, signal, TemplateRef, ViewChild } from '@angular/core'
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop'
import { PageHeaderComponent, PageHeaderMenuItem } from '../page-header/page-header.component'
import { PageContentComponent } from '../page-content/page-content.component'
import { FilterService } from 'src/app/shared/services/filter/filter.service'
import { DateRangeFilter } from 'src/app/shared/services/filter/date-range-filter'
import { dateTimeWithoutTimezone, formatAsUTC, monthOnlyFormat } from 'src/app/shared/utilities/date'
import { ActiveFilterListComponent } from '../table/filters/active-filter-list/active-filter-list.component'
import { DataTableComponent } from '../table/data-table.component'
import { ColDef, GetRowIdFunc } from 'ag-grid-community'
import { catchError, combineLatest, from, NEVER, switchMap, tap } from 'rxjs'
import { DateRangeFilterModalComponent } from '../modals/filters/date-range-filter-modal.component'
import { withHeader } from '../table/headers/table-header.component'
import { ActivatedRoute, Router } from '@angular/router'
import { Location } from '@angular/common'
import { ToastService } from '../toasts/toasts.service'
import { MatMenuModule } from '@angular/material/menu'
import { TBillSettlementsService } from '../settlements/tbill-settlements.service'
import { PaginationParams } from 'src/app/shared/utilities/http-params'
import { TBillSchedule1Rate, TBillSchedule1RateCreateRequest, TBillSchedule1RateUpdateRequest } from '../settlements/tbill-settlements.types'
import { TablePaginationComponent } from '../table/table-pagination/table-pagination.component'
import { TableSortService } from '../table/services/table-sort.service'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { StringFilterModalComponent } from '../modals/filters/string-filter-modal.component'
import { StringFilter } from 'src/app/shared/services/filter/string-filter'
import openModal from 'src/app/shared/services/openModal'
import { ConfirmationModalComponent } from '../modals/confirmation-modal/confirmation-modal.component'
import { LabeledPropertyComponent } from '../labeled-property/labeled-property.component'
import { pipe } from 'lodash/fp'
import { NumberRangeFilter } from 'src/app/shared/services/filter/number-range-filter'
import { actionColumn, withCell } from '../table/cells/with-cell'
import { DateCellComponent } from '../table/cells/date-cell.component'
import { NumberCellComponent } from '../table/cells/number-cell.component'
import { NumberRangeFilterModalComponent } from '../modals/filters/number-range-filter-modal.component'
import { DateDisplayComponent } from '../date-display/date-display.component'
import { NumberDisplayComponent } from '../number-display/number-display-component'
import { CreateTBillSchedule1RateModalComponent } from './create-tbill-schedule-1-rate-modal.component'
import { UpdateTBillSchedule1RateModalComponent } from './update-tbill-schedule-1-rate-modal.component'

@Component({
  selector: 'app-tbill-schedule-1',
  imports: [
    MatMenuModule,
    PageHeaderComponent,
    PageContentComponent,
    LabeledPropertyComponent,
    ActiveFilterListComponent,
    DataTableComponent,
    TablePaginationComponent,
    DateDisplayComponent,
    NumberDisplayComponent,
  ],
  providers: [TableSortService],
  styles: '',
  template: `
    <div app-page-header header="Schedule 1 Rates" [menuItems]="pageHeaderMenuItems"></div>
    <div app-page-content>
        <app-active-filter-list [filterGroup]="constructor.name"></app-active-filter-list>
        <app-data-table [loading]="loading()" [data]="data()" [getRowId]="getRowId" [columnDefs]="columnDefs"></app-data-table>
        <app-table-pagination
            [pageNumber]="pageNumber()"
            [pageSize]="pageSize()"
            [totalResults]="totalResults()"
            (pageNumberChange)="pageNumber.set($event)"
            (pageSizeChange)="pageSize.set($event)"
        />
    </div>

    <ng-template #deleteConfirmation let-ctx="ctx">
        Are you sure you want to delete TBill Schedule 1 Rate?
        @if(ctx) {
            <div app-labeled-property label="Month">
              <div app-date-display [value]="ctx.month" [format]="monthOnlyFormat"></div>
            </div>
            <div app-labeled-property label="Rate ($/(MW\u00A0MTH))">
              <div app-number-display [value]="ctx.rate"></div>
            </div>
        }
    </ng-template>
    `,
})
export class TBillSchedule1Component {
  protected readonly monthOnlyFormat = monthOnlyFormat
  protected readonly dateTimeWithoutTimezone = dateTimeWithoutTimezone

  private readonly router = inject(Router)
  private readonly route = inject(ActivatedRoute)
  private readonly location = inject(Location)
  private readonly toastService = inject(ToastService)
  private readonly filterService = inject(FilterService)
  private readonly sortService = inject(TableSortService)
  private readonly modalService = inject(NgbModal)
  private readonly tbillSettlementsService = inject(TBillSettlementsService)

  @ViewChild('deleteConfirmation')
  deleteConfirmationTemplate: TemplateRef<{ ctx: TBillSchedule1Rate }>

  protected readonly refresh = signal(0)
  protected readonly loading = signal(false)
  protected readonly pageSize = signal(25)
  protected readonly pageNumber = signal(1)
  protected readonly totalResults = signal(0)
  protected readonly data = signal<TBillSchedule1Rate[]>([])
  protected readonly getRowId: GetRowIdFunc<TBillSchedule1Rate> = (data) => formatAsUTC(data.data.month)
  protected readonly paginationParams = computed<PaginationParams>(() => ({
    pageNumber: this.pageNumber(),
    pageSize: this.pageSize(),
  }))

  protected readonly pageHeaderMenuItems: readonly PageHeaderMenuItem[] = [
    { label: 'Create', onClick: () => this.openCreateModal() },
  ]

  protected readonly columnDefs: ColDef[] = [
    pipe(
      withHeader<TBillSchedule1Rate, DateRangeFilterModalComponent>({
        headerName: 'Month',
        field: 'month',
        filterable: true,
        sortable: true,
        filterGroup: TBillSchedule1Component.name,
        modal: DateRangeFilterModalComponent,
        modalInit: {},
      }),
      withCell<DateCellComponent<TBillSchedule1Rate>>(
        DateCellComponent,
        {
          date: (d) => d.month,
          format: monthOnlyFormat,
        },
      ))({
      flex: 1,
      editable: false,
      minWidth: 100,
      width: 175,
    }),
    pipe(
      withHeader<TBillSchedule1Rate, NumberRangeFilterModalComponent>({
        headerName: 'Rate ($/(MW\u00A0MTH))',
        field: 'rate',
        filterable: true,
        sortable: true,
        filterGroup: TBillSchedule1Component.name,
        modal: NumberRangeFilterModalComponent,
        modalInit: {},
      }),
      withCell<NumberCellComponent<TBillSchedule1Rate>>(
        NumberCellComponent,
        {
          num: (d) => d.rate,
        },
      ))({
      editable: false,
      minWidth: 100,
      width: 190,
    }),
    pipe(
      withHeader<TBillSchedule1Rate, DateRangeFilterModalComponent>({
        headerName: 'Created On (STL)',
        field: 'create_time',
        filterable: true,
        sortable: true,
        filterGroup: TBillSchedule1Component.name,
        modal: DateRangeFilterModalComponent,
        modalInit: {},
      }),
      withCell<DateCellComponent<TBillSchedule1Rate>>(
        DateCellComponent,
        {
          date: (d) => d.create_time,
          format: dateTimeWithoutTimezone,
          viewAsTZ: 'America/Chicago',
        },
      ))({
      editable: false,
      minWidth: 100,
      width: 175,
    }),
    pipe(
      withHeader<TBillSchedule1Rate, StringFilterModalComponent>({
        headerName: 'Created By',
        field: 'created_by',
        filterable: true,
        sortable: true,
        filterGroup: TBillSchedule1Component.name,
        modal: StringFilterModalComponent,
        modalInit: {},
      }),
    )({
      editable: false,
      minWidth: 100,
      width: 200,
    }),
    pipe(
      withHeader<TBillSchedule1Rate, DateRangeFilterModalComponent>({
        headerName: 'Last Updated (STL)',
        field: 'last_update_time',
        filterable: true,
        sortable: true,
        filterGroup: TBillSchedule1Component.name,
        modal: DateRangeFilterModalComponent,
        modalInit: {},
      }),
      withCell<DateCellComponent<TBillSchedule1Rate>>(
        DateCellComponent,
        {
          date: (d) => d.last_update_time,
          format: dateTimeWithoutTimezone,
          viewAsTZ: 'America/Chicago',
        },
      ))({
      editable: false,
      minWidth: 100,
      width: 175,
    }),
    pipe(
      withHeader<TBillSchedule1Rate, StringFilterModalComponent>({
        headerName: 'Last Updated By',
        field: 'last_updated_by',
        filterable: true,
        sortable: true,
        filterGroup: TBillSchedule1Component.name,
        modal: StringFilterModalComponent,
        modalInit: {},
      }))({
      editable: false,
      minWidth: 100,
      width: 200,
    }),
    actionColumn<TBillSchedule1Rate>(TBillSchedule1Component.name, {
      actions: [
        {
          name: 'Update',
          onAction: (d: TBillSchedule1Rate) => this.openUpdateModal(d),
        },
        {
          name: 'Delete',
          onAction: (d: TBillSchedule1Rate) => this.openDeleteModal(d),
        },
      ],
    }),
  ]

  constructor() {
    const { dispatch } = this.filterService
    dispatch(FilterService.InitFiltersAction({
      fieldGroup: TBillSchedule1Component.name,
      fields: {
        month: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: false,
            showSeconds: false,
            sendTime: true,
            onlyMonth: true,
            meta: {
              fieldGroup: TBillSchedule1Component.name,
              fieldName: 'month',
              fieldDisplay: 'Month',
            },
          },
        }, dispatch),
        rate: new NumberRangeFilter({
          config: {
            allowRange: true,
            precision: 8,
            meta: {
              fieldGroup: TBillSchedule1Component.name,
              fieldName: 'rate',
              fieldDisplay: '($/(MW\u00A0MTH))',
            },
          },
        }, dispatch),
        create_time: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: true,
            showSeconds: false,
            viewAsTZ: 'America/Chicago',
            meta: {
              fieldGroup: TBillSchedule1Component.name,
              fieldName: 'create_time',
              fieldDisplay: 'Created On (STL)',
            },
          },
        }, dispatch),
        created_by: new StringFilter({
          config: {
            allowMultiple: true,
            meta: {
              fieldGroup: TBillSchedule1Component.name,
              fieldName: 'created_by',
              fieldDisplay: 'Created By',
            },
          },
        }, dispatch),
        last_update_time: new DateRangeFilter({
          config: {
            showOperation: true,
            showRange: true,
            showTime: true,
            showSeconds: false,
            viewAsTZ: 'America/Chicago',
            meta: {
              fieldGroup: TBillSchedule1Component.name,
              fieldName: 'last_update_time',
              fieldDisplay: 'Last Updated (STL)',
            },
          },
        }, dispatch),
        last_updated_by: new StringFilter({
          config: {
            allowMultiple: true,
            meta: {
              fieldGroup: TBillSchedule1Component.name,
              fieldName: 'last_updated_by',
              fieldDisplay: 'Last Updated By',
            },
          },
        }, dispatch),
      },
    }))

    combineLatest([
      toObservable(this.paginationParams),
      this.filterService
        .asParams(TBillSchedule1Component.name)
        .pipe(
          tap(() => {
            if (this.pageNumber() !== 1) {
              this.pageNumber.set(1)
            }
          }),
        ),
      this.sortService.getOrder(),
      toObservable(this.refresh),
    ])
      .pipe(
        takeUntilDestroyed(),
        tap(() => this.loading.set(true)),
        switchMap(([pagination, filters, order]) => this.tbillSettlementsService.getTBillSchedule1Rates(pagination, filters, order)),
        catchError(this.handleLoadingError('Something went wrong loading tbill retailer usage overrides!')),
      )
      .subscribe({
        error: this.handleLoadingError('Something went wrong loading tbill retailer usage overrides!'),
        next: (res) => {
          this.totalResults.set(res.total)
          this.data.set(res.results)
          this.loading.set(false)
        },
      })

    from(this.filterService.manageQueryParms(
      this.router,
      this.route,
      this.location,
      TBillSchedule1Component.name,
    ))
      .pipe(
        switchMap(obs$ => obs$),
        takeUntilDestroyed(),
      )
      .subscribe()
  }

  protected handleLoadingError(defaultMessage: string){
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (e: any) => {
      this.data.set([])
      this.loading.set(false)
      this.toastService.show({ type: 'error', message: e.detail ?? defaultMessage })
      return NEVER
    }
  }

  async openCreateModal() {
    openModal(
      this.modalService,
      CreateTBillSchedule1RateModalComponent,
      { centered: true },
    ).init({
      modalState: {},
      onCancel: (() => {
        this.modalService.dismissAll()
      }),
      onCreate: this.onCreate.bind(this),
    })
  }

  async onCreate(req: TBillSchedule1RateCreateRequest) {
    this.tbillSettlementsService.createTBillSchedule1Rate(req)
      .subscribe({
        error: (e) => {
          this.toastService.show({ type: 'error', message: e.detail })
        },
        complete: () => {
          this.toastService.show({ type: 'success', message: 'Creation successful!' })
          this.modalService.dismissAll()
          this.refresh.update(v => v + 1)
        },
      })
  }

  async openUpdateModal(data: TBillSchedule1Rate) {
    openModal(
      this.modalService,
      UpdateTBillSchedule1RateModalComponent,
      { centered: true },
    ).init({
      data,
      modalState: {
        rate: data.rate,
      },
      onCancel: (() => {
        this.modalService.dismissAll()
      }),
      onUpdate: this.onUpdate.bind(this),
    })
  }

  async onUpdate(market_interval: Date, req: TBillSchedule1RateUpdateRequest) {
    this.tbillSettlementsService.updateTBillSchedule1Rate(market_interval, req)
      .subscribe({
        error: (e) => {
          this.toastService.show({ type: 'error', message: e.detail ?? 'Something went wrong updating this override!' })
        },
        complete: () => {
          this.toastService.show({ type: 'success', message: 'Update successful!' })
          this.modalService.dismissAll()
          this.refresh.update(v => v + 1)
        },
      })
  }

  async openDeleteModal(data: TBillSchedule1Rate) {
    openModal(
      this.modalService,
      ConfirmationModalComponent,
      { centered: true },
    ).init({
      headerText: 'Confirm Deletion',
      confirmButtonText: 'Delete',
      bodyTemplate: this.deleteConfirmationTemplate,
      bodyContext: data,
      onCancel: () => {
        this.modalService.dismissAll()
      },
      onConfirm: () => {
        this.onDelete(data.month)
      },
    })
  }

  async onDelete(month: Date) {
    this.tbillSettlementsService.deleteTBillSchedule1Rate(month)
      .subscribe({
        error: (e) => {
          this.toastService.show({ type: 'error', message: e.detail ?? 'Something went wrong deleting this override!' })
        },
        complete: () => {
          this.toastService.show({ type: 'success', message: 'Deletion successful!' })
          this.modalService.dismissAll()
          this.refresh.update(v => v + 1)
        },
      })
  }
}